import React, { useState, useEffect, useCallback } from "react";
import {
  Activity,
  CheckCircle,
  Clock,
  Server,
  Wrench,
  BookOpen,
  MessageSquare,
  AlertTriangle,
  RefreshCw,
  Zap,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { useToast } from "../../../../hooks/use-toast";
import { GatewayDashboardApi } from "../Api/gatewayApi";
import { HealthData, ServerHealth } from "../interfaces/gatewayInterfaces";
import {
  formatDuration,
  formatTimestamp,
  timeAgo,
  successRateColor,
  latencyColor,
  apiError,
} from "../utils/gatewayUtils";
import GatewayStatCard from "../components/GatewayStatCard";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { Skeleton } from "../../../ui/skeleton";

const REFRESH_INTERVAL_MS = 30000;

function ServerHealthCard({ server }: { server: ServerHealth }) {
  const isActive = server.active;
  const uptimeLabel = server.uptime ? formatUptime(server.uptime) : "-";
  const lastActivity = server.lastActivity ? formatTimestamp(server.lastActivity) : "No activity";

  return (
    <div
      className={cn(
        "rounded-xl border p-4 flex flex-col gap-3 transition-all",
        isActive
          ? "bg-card border-border hover:border-border"
          : "bg-card border-border opacity-70"
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={cn(
              "flex-shrink-0 w-2 h-2 rounded-full",
              isActive ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.6)]" : "bg-muted-foreground/50"
            )}
          />
          <span className="font-semibold text-foreground truncate text-sm">{server.name}</span>
        </div>
        <Badge
          variant="outline"
          className={cn(
            "text-xs flex-shrink-0",
            isActive
              ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/10"
              : "border-border text-muted-foreground bg-muted/60"
          )}
        >
          {isActive ? "ACTIVE" : "INACTIVE"}
        </Badge>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col">
          <span className="text-xs text-muted-foreground uppercase tracking-wider">Uptime</span>
          <span className="text-sm font-medium text-foreground/80">{uptimeLabel}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-xs text-muted-foreground uppercase tracking-wider">Tools</span>
          <span className="text-sm font-medium text-primary">{server.toolCount ?? 0}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-xs text-muted-foreground uppercase tracking-wider">Resources</span>
          <span className="text-sm font-medium text-violet-400">{server.resourceCount ?? 0}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-xs text-muted-foreground uppercase tracking-wider">Prompts</span>
          <span className="text-sm font-medium text-amber-400">{server.promptCount ?? 0}</span>
        </div>
      </div>

      <div className="flex flex-col border-t border-border pt-2">
        <span className="text-xs text-muted-foreground uppercase tracking-wider">Last Activity</span>
        <span className="text-xs text-muted-foreground mt-0.5">{lastActivity}</span>
      </div>
    </div>
  );
}

function formatUptime(ms: number): string {
  if (!ms || ms <= 0) return "-";
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}

function HealthPageSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="rounded-xl border border-border bg-card shadow-sm p-5 flex flex-col gap-3"
          >
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="flex flex-col gap-1">
              <Skeleton className="h-7 w-20" />
              <Skeleton className="h-3 w-28" />
            </div>
          </div>
        ))}
      </div>

      <div>
        <Skeleton className="h-5 w-32 mb-4" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48 rounded-xl" />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-40 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

export default function GatewayHealthPage() {
  const { toast } = useToast();
  const [data, setData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const fetchHealth = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const result = await GatewayDashboardApi.health();
      setData(result);
      setLastRefreshed(new Date());
    } catch (e: any) {
      toast({
        title: "Failed to load health data",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchHealth(true);
    const timer = setInterval(() => fetchHealth(false), REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchHealth]);

  const successRate = data?.successRate ?? 0;
  const successColorClass = successRateColor(successRate);

  const activeServers = data?.activeServers ?? 0;
  const totalServers = data?.servers?.length ?? 0;

  const lat = data?.latency ?? { p50: undefined, p95: undefined, p99: undefined };

  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-foreground">Health Overview</h1>
        </div>
        <HealthPageSkeleton />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Health Overview
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Gateway status, server health, and performance metrics
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchHealth(true)}
          className="border-border text-foreground/80 hover:text-foreground hover:border-border"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <GatewayStatCard
          icon={<CheckCircle className="w-5 h-5" />}
          label="Success Rate (24h)"
          value={`${successRate}%`}
          colorClass={cn(
            "flex items-center justify-center w-10 h-10 rounded-lg bg-muted/80",
            successColorClass
          )}
        />

        <GatewayStatCard
          icon={<Activity className="w-5 h-5" />}
          label="Requests / min"
          value={data?.requestsPerMinute ?? 0}
          colorClass="flex items-center justify-center w-10 h-10 rounded-lg bg-muted/80 text-primary"
        />

        <GatewayStatCard
          icon={<Clock className="w-5 h-5" />}
          label="Avg Latency (24h)"
          value={formatDuration(data?.avgDurationMs ?? data?.avgLatency)}
          colorClass="flex items-center justify-center w-10 h-10 rounded-lg bg-muted/80 text-amber-400"
        />

        <GatewayStatCard
          icon={<Server className="w-5 h-5" />}
          label="Active Servers"
          value={`${activeServers} / ${totalServers}`}
          colorClass="flex items-center justify-center w-10 h-10 rounded-lg bg-muted/80 text-violet-400"
        />
      </div>

      <div>
        <h2 className="text-sm font-semibold text-foreground/80 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Server className="w-4 h-4 text-primary" />
          Server Health
        </h2>

        {!data?.servers || data.servers.length === 0 ? (
          <div className="rounded-xl border border-border bg-card shadow-sm p-10 text-center">
            <Server className="w-10 h-10 text-muted-foreground/70 mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">No servers configured</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {data.servers.map((server) => (
              <ServerHealthCard key={server.name ?? server.configKey} server={server} />
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground/80 flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400" />
              Latency Percentiles (24h)
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {(["p50", "p95", "p99"] as const).map((pct) => (
              <div key={pct} className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium w-8">
                  {pct}
                </span>
                <div className="flex-1 mx-3 h-1 rounded-full bg-muted overflow-hidden">
                  <div
                    className={cn(
                      "h-full rounded-full",
                      pct === "p50"
                        ? "bg-emerald-400/60"
                        : pct === "p95"
                        ? "bg-amber-400/60"
                        : "bg-red-400/60"
                    )}
                    style={{
                      width:
                        lat[pct]
                          ? `${Math.min(100, (lat[pct]! / (lat.p99 || 1)) * 100)}%`
                          : "0%",
                    }}
                  />
                </div>
                <span
                  className={cn(
                    "text-sm font-semibold w-16 text-right",
                    latencyColor(pct)
                  )}
                >
                  {formatDuration(lat[pct])}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground/80 flex items-center gap-2">
              <Wrench className="w-4 h-4 text-primary" />
              Capability Inventory
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wrench className="w-3.5 h-3.5 text-primary" />
                <span className="text-sm text-muted-foreground">Tools</span>
              </div>
              <span className="text-sm font-semibold text-primary">
                {data?.totalTools ?? 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BookOpen className="w-3.5 h-3.5 text-violet-400" />
                <span className="text-sm text-muted-foreground">Resources</span>
              </div>
              <span className="text-sm font-semibold text-violet-400">
                {data?.totalResources ?? 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-sm text-muted-foreground">Prompts</span>
              </div>
              <span className="text-sm font-semibold text-amber-400">
                {data?.totalPrompts ?? 0}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground/80 flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-400" />
              Activity (24h)
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Events</span>
              <span className="text-sm font-semibold text-foreground">
                {data?.recentEventCount ?? data?.totalEvents ?? 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                <span className="text-sm text-muted-foreground">Errors</span>
              </div>
              <span className="text-sm font-semibold text-red-400">
                {data?.recentErrorCount ?? data?.totalErrors ?? 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-primary" />
                <span className="text-sm text-muted-foreground">In-Flight</span>
              </div>
              <span className="text-sm font-semibold text-primary">
                {data?.inFlightCount ?? 0}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
